import type { ApiPayload, QueryPayload, RequestOptions, ProcessResponseOptions } from "./types";
import { ContentType } from "./ContentType";
import { createBaseApiService, type IBaseApiService } from "./BaseApiServiceFactory";
import { invalidateBaseUrl } from "./helpers/configHelpers";
export { ContentType };

export type IRef = {
	response?: Response;
};
abstract class BaseApiServiceClass {
	private readonly baseApi: IBaseApiService = createBaseApiService();

	public static invalidateBaseUrl(): void {
		invalidateBaseUrl();
	}

	protected getBaseUrl(): string {
		return this.baseApi.getBaseUrl();
	}

	protected buildHeaders(contentType: ContentType): Headers {
		return this.baseApi.buildHeaders(contentType);
	}

	protected buildBody(body: ApiPayload): string {
		return this.baseApi.buildBody(body);
	}

	protected appendJsonSearchParams(url: URL, params: QueryPayload): void {
		this.baseApi.appendJsonSearchParams(url, params);
	}

	protected async getRequest<T>(url: URL, options?: RequestOptions): Promise<T> {
		return this.baseApi.getRequest<T>(url, options);
	}

	protected async postRequest<T>(url: URL, body: ApiPayload = {}, _token?: string): Promise<T> {
		return this.baseApi.postRequest<T>(url, body, _token);
	}

	protected async postRequestFormData<T>(url: URL, body: FormData): Promise<T> {
		return this.baseApi.postRequestFormData<T>(url, body);
	}

	protected async putRequest<T>(url: URL, body: ApiPayload = {}, _token?: string): Promise<T> {
		return this.baseApi.putRequest<T>(url, body, _token);
	}

	protected async patchRequest<T>(url: URL, body: ApiPayload = {}): Promise<T> {
		return this.baseApi.patchRequest<T>(url, body);
	}

	protected async deleteRequest<T>(url: URL, body: ApiPayload = {}, _token?: string): Promise<T> {
		return this.baseApi.deleteRequest<T>(url, body, _token);
	}

	protected async putFormDataRequest<T>(url: URL, body: FormData, _token?: string): Promise<T> {
		return this.baseApi.putFormDataRequest<T>(url, body, _token);
	}

	protected async processResponse<T>(
		response: Response,
		request: () => Promise<Response>,
		options?: ProcessResponseOptions,
	): Promise<T> {
		return this.baseApi.processResponse<T>(response, request, options);
	}

	protected onError(error: unknown): void {
		this.baseApi.onError(error);
	}
}

export interface IResponse {
	http_status: number;
}

// Export named first, then default to avoid Webpack initialization order issues
export { BaseApiServiceClass as BaseApiService };
export default BaseApiServiceClass;
